import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  createTheme,
  Divider,
  IconButton,
  Stack,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState, type Dispatch, type FC, type SetStateAction } from "react";
import "react-calendar/dist/Calendar.css";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";

import { type Status, type Opportunity, Category, Population } from "@entities";
import { opportunityRepo } from "@repositories";
import { useAsyncEffect } from "@shared/frontend/hooks";
import {
  OPPORTUNITY_TITLE_MAX_LENGTH,
  VOLUNTEER_DESCRIPTION_MAX_LENGTH,
} from "@shared/helpers/constants";
import {
  MATCH_WEIGHT_MAX,
  MATCH_WEIGHT_MIN,
} from "@shared/helpers/match-weight";

import { GeneralAlert } from "../../components/alert/GeneralAlert";
import { useOrganization } from "../../contexts/OrganizationProvider";
import {
  REQUEST_DEFAULT_FIELDS,
  REQUEST_DEFAULT_VALUES,
  useRequest,
} from "../../contexts/RequestProvider";
import { OpportunityVolunteerMode } from "@shared/helpers/opportunity-volunteer-mode";
import { resolveVolunteerRegistrationFlowForAdmin } from "@shared/helpers/volunteer-registration-outcome";
import { getStatusOptions } from "../../generic-functions/get-status-options.function";
import FormProvider, { useForm } from "../../helpers/forms/FormProvider";
import { provide } from "../../helpers/wrap-and-provide";
import OrganizationRequestButtons from "./OrganizationRequestButtons";
import OrganizationRequestMainSection from "./OrganizationRequestMainSection";
import RequestMoreDetails from "./RequestMoreDetails";
import { OpportunityRequesterAssociationSection } from "./OpportunityRequesterAssociationSection";
import { OpportunityHandlingControls } from "../../components/opportunity-handling/OpportunityHandlingControls";

import useQuestion from "../../dialog-components/QuestionDialog";
import "./OrganizationRequest.scss";

export interface OrganizationRequestSaveResult {
  newId?: string;
  isDuplicate?: boolean;
}

export interface OrganizationRequestProps {
  setIsAddEvent?: Dispatch<SetStateAction<boolean>>;
  isEventCard?: boolean;
  selectedList?: string;
  isEdit?: boolean;
  isGroupEvent?: boolean;
  /** @deprecated Use showExtendedLayout / isStandalonePage */
  fullPage?: boolean;
  showExtendedLayout?: boolean;
  isStandalonePage?: boolean;
  eventData?: Opportunity;
  setIsAddAidRelated?: Dispatch<SetStateAction<boolean>>;
  onSaveSuccess?: (result?: OrganizationRequestSaveResult) => void;
}

export type FormFields = Partial<Opportunity>;

const theme = createTheme({
  palette: {
    primary: {
      main: "#05204CF2",
    },
    secondary: {
      main: "#00b4d8",
    },
  },
});

export const REQUEST_VALIDATION_SCHEMA = yup.object().shape({
  title: yup
    .string()
    .min(2, "הכותרת צריכה להכיל לפחות 2 תווים")
    .max(
      OPPORTUNITY_TITLE_MAX_LENGTH,
      `הכותרת יכולה להכיל עד ${OPPORTUNITY_TITLE_MAX_LENGTH} תווים`,
    )
    .required("שדה זה הינו שדה חובה"),
  description: yup
    .string()
    .min(5, "התיאור צריך להכיל לפחות 5 תווים")
    .max(
      VOLUNTEER_DESCRIPTION_MAX_LENGTH,
      `התיאור יכול להכיל עד ${VOLUNTEER_DESCRIPTION_MAX_LENGTH} תווים`,
    )
    .required("שדה זה הינו שדה חובה"),
  imageId: yup.string().optional(),
  location: yup
    .string()
    .min(2, "שדה זה צריך להכיל 2 תווים לפחות")
    .required("שדה זה הינו שדה חובה"),
  coordinates: yup.object().shape({
    lat: yup.number(),
    lng: yup.number(),
  }),
  destinationCoordinates: yup.object().shape({
    lat: yup.number(),
    lng: yup.number(),
  }),
  destination: yup
    .string()
    .min(2, "שדה זה צריך להכיל 2 תווים לפחות")
    .required("שדה זה הינו שדה חובה")
    .nullable(),
  amount: yup
    .number()
    .min(1, "בין 1 ל-1000")
    .max(1000, "בין 1 ל-1000")
    .required("שדה זה הינו שדה חובה"),
  defaultMatchWeight: yup
    .number()
    .integer(`בין ${MATCH_WEIGHT_MIN} ל-${MATCH_WEIGHT_MAX}`)
    .min(MATCH_WEIGHT_MIN, `בין ${MATCH_WEIGHT_MIN} ל-${MATCH_WEIGHT_MAX}`)
    .max(MATCH_WEIGHT_MAX, `בין ${MATCH_WEIGHT_MIN} ל-${MATCH_WEIGHT_MAX}`)
    .required("שדה זה הינו שדה חובה"),
  dailyVolunteerTarget: yup
    .number()
    .transform((value, original) =>
      original === "" || original == null || Number.isNaN(value) ? null : value,
    )
    .nullable()
    .integer("יעד יומי חייב להיות מספר שלם")
    .min(1, "יעד יומי חייב להיות לפחות 1, או ריק"),
  contactName: yup
    .string()
    .min(2, "שדה זה צריך להכיל 2 תווים לפחות")
    .nullable(),
  contactPhoneNumber: yup
    .string()
    .length(10, "שדה זה צריך להכיל 10 תווים בדיוק")
    .nullable(),
  contactPosition: yup.string().nullable(),
  comments: yup.string(),
});

const OrganizationRequest: FC<OrganizationRequestProps> = ({
  isEdit,
  isEventCard,
  fullPage,
  showExtendedLayout,
  isStandalonePage,
  isGroupEvent,
  eventData,
  setIsAddAidRelated = () => { },
  setIsAddEvent,
  onSaveSuccess,
}) => {
  const resolvedShowExtended = showExtendedLayout ?? fullPage ?? true;
  const resolvedStandalone = isStandalonePage ?? fullPage ?? true;
  const { state: newEventData } = useLocation();
  const {
    currentEvent: requestDetails,
    setCurrentEvent: setRequestDetails,
    resetRequest,
    setIsTouched,
    isTouched: tochedFromProvider,
    lastAiApplyAt,
  } = useRequest();

  const { values, setValues, touched, setTouched } = useForm<FormFields>();
  const [statusOptions, setStatusOptions] = useState<Status[]>([]);
  const navigate = useNavigate();
  const { currOrganization } = useOrganization();
  const isTouched = Object.keys(touched).length > 0;
  const blocker = useBlocker(isTouched && tochedFromProvider);
  const question = useQuestion();
  const hydratedOpportunityIdRef = useRef<string | null>(null);

  const syncOpportunityUpdatedAt = async (opportunityId: string) => {
    const fresh = await opportunityRepo.findId(opportunityId);
    if (!fresh?.updatedAt) return;
    setRequestDetails((prev) => ({ ...prev, updatedAt: fresh.updatedAt }));
  };

  useEffect(() => {
    setIsTouched(isTouched);
  }, [isTouched]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      question(
        "האם אתה בטוח שברצונך לעזוב את הדף?",
        "השינויים לא נשמרו והדבר עלול להוביל למחיקה של מידע",
        {
          confirmText: "כן, עזוב",
          cancelText: "לא, להישאר",
        },
      ).then((result) => {
        if (result) {
          blocker.proceed();
          navigate(blocker.location.pathname);
          resetRequest();
        } else {
          blocker.reset();
          setTouched({});
        }
      });
    }
  }, [blocker.state]);

  const currentEditEvent =
    eventData || (newEventData as Opportunity | undefined);

  useEffect(() => {
    return () => {
      resetRequest();
    };
  }, []);

  /** טופס חדש (הוספה) – איפוס לברירות מחדל כולל SMS_DETAILS. */
  useEffect(() => {
    if (!currentEditEvent && !isEdit) {
      resetRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  useEffect(() => {
    // We need to set only specific values to the form
    setRequestDetails((prev) => ({
      ...prev,
      title: values.title,
      description: values.description,
      amount: values.amount,
      defaultMatchWeight: values.defaultMatchWeight,
      dailyVolunteerTarget: (() => {
        if (
          values.dailyVolunteerTarget == null ||
          (values.dailyVolunteerTarget as unknown) === ""
        ) {
          return null;
        }
        const parsed = Number(values.dailyVolunteerTarget);
        return Number.isFinite(parsed) ? parsed : null;
      })(),
      comments: values.comments,
      location: values.location,
      coordinates: values.coordinates,
      destination: values.destination,
      destinationCoordinates: values.destinationCoordinates,
      progressStatus: values.progressStatus,
      managerUrgency: values.managerUrgency,
      callCenterUrgency: values.callCenterUrgency,
      callCenterDifficulty: values.callCenterDifficulty,
      isRemote: values.isRemote,
      targetOrganizationName: values.targetOrganizationName,
      minAge: values.minAge,
      maxAge: values.maxAge,
    }));
  }, [
    values.title,
    values.description,
    values.amount,
    values.defaultMatchWeight,
    values.dailyVolunteerTarget,
    values.comments,
    values.location,
    JSON.stringify(values.coordinates),
    values.destination,
    JSON.stringify(values.destinationCoordinates),
    values.progressStatus,
    values.managerUrgency,
    values.callCenterUrgency,
    values.callCenterDifficulty,
    values.isRemote,
    values.targetOrganizationName,
    values.minAge,
    values.maxAge,
  ]);

  useEffect(() => {
    if (!isGroupEvent || !currOrganization || currentEditEvent?.id) {
      return;
    }
    setRequestDetails((prev) => ({
      ...prev,
      volunteerMode: OpportunityVolunteerMode.GROUP,
      isValidationRequired: true,
    }));
  }, [isGroupEvent, currOrganization, currentEditEvent?.id]);

  useEffect(() => {
    if (!currentEditEvent?.id) {
      hydratedOpportunityIdRef.current = null;
      return;
    }
    // eventData comes from React Query; refetch on window focus yields a new object
    // reference and would wipe unsaved edits if we always re-hydrate from the server.
    // Guard: after AI apply, allow the UI to settle before any server hydration.
    if (Date.now() - lastAiApplyAt < 2500) return;
    if (Object.keys(touched).length > 0 || tochedFromProvider) return;
    // Hydrate once per opportunity per edit session — not on every background refetch.
    if (hydratedOpportunityIdRef.current === currentEditEvent.id) return;
    hydratedOpportunityIdRef.current = currentEditEvent.id;

    setRequestDetails((prev) => ({
      ...prev,
      ...currentEditEvent,
      volunteerRegistrationFlow: resolveVolunteerRegistrationFlowForAdmin(
        currentEditEvent,
      ),
      associationRequested: currentEditEvent.associationRequestedId
        ? (currentEditEvent.associationRequested ?? prev.associationRequested)
        : undefined,
      associationRequestedId: currentEditEvent.associationRequestedId ?? null,
      minAge: currentEditEvent.minAge || 6,
      destination: currentEditEvent?.destination || null,
      destinationCoordinates:
        currentEditEvent?.destinationCoordinates ||
        REQUEST_DEFAULT_FIELDS.destinationCoordinates,
      selectedTeams:
        currentEditEvent.teams?.filter(Boolean)?.map((t) => t.team!) ||
        prev.selectedTeams,
      selectedCategories: (() => {
        const catalogIds = new Set(
          (currOrganization?.categories ?? []).map((category) => category.id),
        );
        const loaded = currentEditEvent.categories
          ?.map((row) => row.category)
          .filter((category): category is NonNullable<typeof category> =>
            Boolean(
              category?.id &&
                (catalogIds.size === 0 || catalogIds.has(category.id)),
            ),
          );
        return loaded ?? prev.selectedCategories;
      })(),
      selectedPopulations:
        currentEditEvent.populations?.filter(Boolean)?.map((p) => p.population!) ||
        prev.selectedPopulations,
      progressStatus: currentEditEvent.progressStatus || (null as any),
      targetOrganizationName: currentEditEvent?.targetOrganizationName,
    }));
    setValues((prev) => ({
      ...prev,
      ...currentEditEvent,
      minAge: currentEditEvent.minAge || 6,
      destination: currentEditEvent?.destination || null,
      destinationCoordinates:
        currentEditEvent?.destinationCoordinates ||
        REQUEST_DEFAULT_FIELDS.destinationCoordinates,
      progressStatus: currentEditEvent?.progressStatus?.id || null,
      managerUrgency: currentEditEvent?.managerUrgency || "STANDARD",
      callCenterUrgency: currentEditEvent?.callCenterUrgency ?? 0,
      callCenterDifficulty: currentEditEvent?.callCenterDifficulty ?? 3,
      isRemote: currentEditEvent?.isRemote || false,
    }));
  }, [
    currentEditEvent,
    touched,
    tochedFromProvider,
    lastAiApplyAt,
    setRequestDetails,
    setValues,
  ]);

  useAsyncEffect(async () => {
    if (!statusOptions.length) {
      const status = await getStatusOptions(
        "MISSION",
        currOrganization?.id ?? "",
      );
      setStatusOptions(status);
    }
  }, [currOrganization]);

  return (
    <ThemeProvider theme={theme}>
      <Stack
        width="100%"
        height="100%"
        flex={1}
        sx={{ minHeight: 0, overflow: "hidden" }}
      >
        {requestDetails.id && !isEventCard && (
          <Stack
            width="100%"
            px={2}
            pt={1.5}
            flexShrink={0}
            alignItems="flex-start"
          >
            <OpportunityHandlingControls
              variant="header"
              opportunityId={requestDetails.id}
              handlingManagerId={requestDetails.handlingManagerId}
              handlingManagerName={requestDetails.handlingManager?.name}
              handlingSince={requestDetails.handlingSince}
              onChanged={() => {
                if (requestDetails.id) {
                  void syncOpportunityUpdatedAt(requestDetails.id);
                }
              }}
            />
          </Stack>
        )}
        <Stack
          className={`organization-request-layout${resolvedShowExtended ? " organization-request-layout--extended" : ""}`}
          position="relative"
          direction="row"
          justifyContent={resolvedShowExtended ? "flex-start" : "space-evenly"}
          alignItems="stretch"
          width="100%"
          flex={1}
          sx={{ minHeight: 0, height: "100%", overflow: "hidden" }}
        >
          {resolvedStandalone && (
            <Tooltip placement="right" title="יציאה">
              <IconButton
                onClick={() => {
                  navigate(-1);
                  resetRequest();
                }}
                sx={{
                  position: "absolute",
                  top: "1%",
                  right: "0",
                  zIndex: 2,
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Tooltip>
          )}
          <OrganizationRequestMainSection
            setIsAddEvent={setIsAddEvent}
            isEdit={isEdit}
            showExtendedLayout={resolvedShowExtended}
            onSaveSuccess={onSaveSuccess}
            isStandalonePage={resolvedStandalone}
            setIsAddAidRelated={setIsAddAidRelated}
          />
          {resolvedShowExtended && (
            <Divider
              className="organization-request-layout-divider"
              sx={{ margin: "0 1vw", borderWidth: "2px", flexShrink: 0 }}
              orientation="vertical"
            />
          )}
          {resolvedShowExtended && (
            <Stack
              className="organization-request-more-details"
              alignItems="stretch"
              flex="1 1 40%"
              sx={{ minHeight: 0, height: "100%", overflow: "hidden", minWidth: 0 }}
            >
              <Stack
                className="organization-request-more-details-scroll"
                gap={2}
                width="100%"
                sx={{ flex: 1, minHeight: 0, overflowY: "auto", pb: 2, px: 1 }}
              >
                <Typography fontWeight={700} fontSize="larger" color="primary">
                  הגדרות נוספות
                </Typography>
                <OpportunityRequesterAssociationSection
                  setIsAddAidRelated={setIsAddAidRelated}
                />
                <RequestMoreDetails
                  setRequestDetails={setRequestDetails}
                  requestDetails={requestDetails}
                  isGroupEvent={!!isGroupEvent}
                />
              </Stack>
              <Stack
                className="organization-request-more-details-actions"
                width="100%"
                direction="row"
                alignItems="center"
                justifyContent="end"
                gap={2}
                sx={{
                  flexShrink: 0,
                  px: 2.5,
                  py: 1.5,
                  borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                }}
              >
                <OrganizationRequestButtons
                  isStandalonePage={resolvedStandalone}
                  isEdit={isEdit}
                  setIsAddEvent={setIsAddEvent}
                  onSaveSuccess={onSaveSuccess}
                />
              </Stack>
            </Stack>
          )}
          <GeneralAlert />
        </Stack>
      </Stack>
    </ThemeProvider>
  );
};

export default provide([
  FormProvider,
  {
    // Important: the form is the source-of-truth for many fields (title/description etc.).
    // Use the full request defaults so we don't overwrite RequestProvider with `undefined`.
    initialValues: REQUEST_DEFAULT_FIELDS,
    onSubmit: () => { },
    validationSchema: REQUEST_VALIDATION_SCHEMA,
    className: "organization-request-form",
  },
])(OrganizationRequest);
